import { randId, friendlyNumber } from 'src/helpers/globals'
import { store } from 'src/store'
import { AUD } from '@dinero.js/currencies'
import {
  dinero,
  multiply,
  subtract,
  add,
  equal,
  toDecimal,
  transformScale,
  halfEven,
  up,
  down,
  allocate
} from 'dinero.js'

export const TAX_RATE = 10
export const TAX_FREE_RATE = 0

/**
 * Creates a displayable currency string, including a currency code if requested.
 * The currency code is pulled from the currency object itself
 * @param d Currency Object
 * @return Currency value with or without currency code
 */
export function toDisplayableCurrency (d) {
  return toDecimal(d, ({ value, currency }) => friendlyNumber(value, true))
}

/**
 * Fetches Dinero underlying rounding method based on ENUM
 * @return Currency value with or without currency code
 */
export function getRoundingMethod () {
  switch (store.getters['company/getCompany'].rounding_method) {
    case 'UP':
      return up
    case 'DOWN':
      return down
    default:
    case 'HALF_EVEN':
      return halfEven
  }
}

/**
 * Converts a floating point number to a Dinero currency object
 * The currency is used to extract the base and exponent for unit conversion
 * @param float Float value to convert
 * @param currency Currency to convert to from (dinero/currencies)
 * @return Dinero object with converted currency
 */
export function dineroFromFloat (float, currency = null) {
  let amount = 0
  currency = currency || AUD
  if (!isNaN(float)) {
    const factor = currency.base ** currency.exponent
    amount = Math.round(float * factor)
  }

  return dinero({ amount, currency })
}

/**
 * Converts a dinero currency object to a floating point number
 * The floating point number will preserve the minor units
 * @param value Dinero object
 * @return Converted floating point number
 */
export function floatFromDinero (value) {
  if (!isNaN(value)) {
    return value
  }
  return parseFloat(toDecimal(value))
}

/**
 * Initialises an empty dinero object with a value of $0
 * The default currency is AUD, and can be changed with the parameter
 * @param currency Dinero currency object from (dinero/currencies)
 * @return Intialised zero-value Dinero object
 */
export function initEmptyDineroObj (currency = AUD) {
  return dinero({ amount: 0, currency })
}

/**
 * Calculates exclusive line price based on the lines unit price and tax rate
 * The tax portion is calculated using ratios and Dineros underlying allocate() function
 * which distributes currency evenly across ratios and preserves minor amounts
 * @param unitPrice Dinero object with line base price and currency
 * @param taxRate Floating point tax percentage
 * @return Dinero object with calculated line price
 */
function calculateExclusiveLineUnitPrice (unitPrice, taxRate) {
  if (taxRate > 0) {
    const ratios = [
      { amount: taxRate, scale: AUD.exponent },
      { amount: 100 - taxRate, scale: AUD.exponent }
    ]
    const [tax] = allocate(unitPrice, ratios)
    return transformScale(add(unitPrice, tax), AUD.exponent, getRoundingMethod())
  }
  return unitPrice
}

/**
 * Calculates multiplier for tax rate
 * @param rate Floating point percentage
 * @return Floating point multiplier
 */
function inclusiveTaxRateOpposite (rate) {
  return (rate / 100) + 1
}

/**
 * Converts inclusive price to exclusive price using Dinero price and tax rate
 * preserving the nominated rounding method
 * The tax portion is calculated using ratios and Dineros underlying allocate() function
 * which distributes currency evenly across ratios and preserves minor amounts
 * @param price Dinero object with line base price and currency
 * @param rate Floating point tax percentage
 * @return Dinero object with calculated line price
 */
function inclusivePriceToExclusive (price, rate) {
  const val = (1 / inclusiveTaxRateOpposite(rate)) * 100
  const ratios = [
    { amount: val, scale: AUD.exponent },
    { amount: 100 - val, scale: AUD.exponent }
  ]
  const [tax] = allocate(price, ratios)
  return transformScale(tax, AUD.exponent, getRoundingMethod())
}

/**
 * Calculates inclusive tax using Dinero price and tax rate
 * preserving the nominated rounding method
 * @param price Dinero object with line base price and currency
 * @param rate Floating point tax percentage
 * @return Dinero object with calculated line price
 */
function calculateInclusiveTax (price, rate) {
  const tax = subtract(price, inclusivePriceToExclusive(price, rate))
  return transformScale(tax, AUD.exponent, getRoundingMethod())
}

/**
 * Calculates exclusive tax using Dinero price and tax rate
 * preserving the nominated rounding method
 * The tax portion is calculated using ratios and Dineros underlying allocate() function
 * which distributes currency evenly across ratios and preserves minor amounts
 * @param price Dinero object with line base price and currency
 * @param rate Floating point tax percentage
 * @return Dinero object with calculated line price
 */
function calculateExclusiveTax (price, rate) {
  const ratios = [
    { amount: rate, scale: AUD.exponent },
    { amount: 100 - rate, scale: AUD.exponent }
  ]
  const [tax] = allocate(price, ratios)
  return transformScale(tax, AUD.exponent, getRoundingMethod())
}

/**
 * Get the correct tax percentage rate based on a line
 * @param itemTaxObj Tax Object with tax information pulled from inventory item
 * @param withTax Boolean value to return tax or tax-free rates
 * @return Floating point tax rate
 */
export function getTaxRate (itemTaxObj, withTax) {
  if (itemTaxObj && ((itemTaxObj.rate === TAX_RATE && withTax) || (itemTaxObj.rate === TAX_FREE_RATE && !withTax))) {
    return itemTaxObj.rate
  }

  return withTax ? TAX_RATE : TAX_FREE_RATE
}

/**
 * Multiply money object by arbitrary float (such as quantity or discount rate)
 * @param money Dinero money object
 * @param float Arbitrary float value
 * @return Manipulated money object
 */
export function multiplyMoneyByFloat (money, float) {
  const decimalPlaces = float % 1 !== 0 ? float.toString().split('.')[1].length : 0
  const integerBase = 10

  const factor = integerBase ** decimalPlaces
  const amount = Math.round(float * factor)
  return multiply(money, { amount, scale: decimalPlaces })
}

/**
 * Convert a unit price from being cast as an old taxType to a
 * new taxType (INCLUSIVE, EXCLUSIVE, NONE)
 * @param newVal String of old tax status
 * @param oldVal String of new tax status
 * @param unitPrice Floating point line unit price
 * @param taxRate Floating point percentages
 * @return Floating point line unit price
 */
// Convert a unit price from being cast as an old taxType to a new taxType (INCLUSIVE, EXCLUSIVE, NONE)
export function convertUnitPriceForTaxType (newVal, oldVal, unitPrice, taxRate) {
  if (newVal === oldVal) {
    return unitPrice
  }
  if (oldVal === 'INCLUSIVE' && (newVal === 'EXCLUSIVE' || newVal === 'NONE')) {
    unitPrice = dineroFromFloat(unitPrice)
    return parseFloat(toDecimal(inclusivePriceToExclusive(unitPrice, taxRate)))
  }
  else if (newVal === 'INCLUSIVE' && (oldVal === 'EXCLUSIVE' || oldVal === 'NONE')) {
    unitPrice = dineroFromFloat(unitPrice)
    return parseFloat(toDecimal(calculateExclusiveLineUnitPrice(unitPrice, taxRate)))
  }
  return unitPrice
}

/**
 * Generates a new standardised line item
 * @param docTaxType String of currency document tax status (INCLUSIVE, EXCLUSIVE or NONE)
 * @param editing Boolean on whether the document is being edited. Used for markups
 * @param docDiscountRate Floating point percentage of transactional discount
 * @param params Standard JSON of line item parameters:
 * (item_id, description, unit_price, quantity,
 * with_tax, discount_rate, item_account, item_tax, inventory_item)
 * @param lineTaxType String of line tax status (INCLUSIVE, EXCLUSIVE or NONE)
 * @return Line Item Object
 */
// Generate a new standardised line item
export function generateLineItem (docTaxType, editing = false, docDiscountRate = 0, params = {}, lineTaxType = 'NONE') {
  const item = {
    uuid: randId(),
    item_id: params.item_id || null,
    description: params.description || null,
    unit_price: params.unit_price ?? 0,
    quantity: params.quantity ?? 1,
    with_tax: params.with_tax ?? store.getters['company/getCompany'].gst_registered,
    discount_rate: docDiscountRate > 0 ? docDiscountRate : (params.discount_rate ?? 0),
    item_account: params.item_account || null,
    item_tax: params.item_tax || null,
    inventory_item: params.inventory_item || null,
    marked_up_percentage: params.marked_up_percentage || 0
  }
  // Convert the unit_price to match the document if applicable
  item.unit_price = convertUnitPriceForTaxType(
    docTaxType,
    lineTaxType,
    item.unit_price,
    getTaxRate(item.item_tax, item.with_tax)
  )
  let unitPrice = dineroFromFloat(item.unit_price)

  // Apply markup if allowed
  if (item.marked_up_percentage > 0) {
    const markupAmount = multiplyMoneyByFloat(unitPrice, (item.marked_up_percentage / 100))
    unitPrice = add(unitPrice, markupAmount)
    item.unit_price = floatFromDinero(unitPrice)
  }
  return item
}

export default {
  methods: {
    /**
     * Calculates all document totals given line items and tax status
     * @param docTaxType String of currency document tax status (INCLUSIVE, EXCLUSIVE or NONE)
     * @param lines Array of Line Item objects
     * @return JSON object of calculated values
     */
    calculateDocumentTotals (lines, docTaxType) {
      let subtotal = initEmptyDineroObj()
      let subtotalIncDiscounts = initEmptyDineroObj()
      let taxAmt = initEmptyDineroObj()
      let discountAmt = initEmptyDineroObj()

      for (const line of lines) {
        const calculations = this.calculateLineTotals(line, docTaxType, line.discount_rate)
        subtotal = add(subtotal, calculations.totalBeforeTax)
        subtotalIncDiscounts = add(subtotalIncDiscounts, calculations.totalBeforeTaxIncDiscount)
        taxAmt = add(taxAmt, calculations.taxAmtIncDiscount)
        discountAmt = add(discountAmt, calculations.discountAmt)
      }

      let discountRate = 0
      if (!equal(subtotal, initEmptyDineroObj())) {
        const tempDiscAmt = toDecimal(discountAmt)
        const tempSubAmt = toDecimal(subtotal)
        discountRate = tempDiscAmt / tempSubAmt
      }

      const totalAfterDiscounts = subtract(subtotal, discountAmt)
      let finalTotal = totalAfterDiscounts
      if (docTaxType === 'EXCLUSIVE') {
        finalTotal = add(totalAfterDiscounts, taxAmt)
      }

      return {
        subtotal,
        subtotalIncDiscounts,
        discountRate: discountRate * 100,
        discountAmt,
        totalAfterDiscounts,
        taxAmt,
        finalTotal
      }
    },
    /**
     * Calculates all line totals given Line Item object and document tax status
     * @param docTaxType String of currency document tax status (INCLUSIVE, EXCLUSIVE or NONE)
     * @param line Line Item object
     * @return JSON object of calculated values
     */
    calculateLineTotals (line, docTaxType) {
      const dineroRoundingMethod = getRoundingMethod()
      const taxRatePercent = getTaxRate(line.item_tax, line.with_tax)

      const unitPrice = transformScale(dineroFromFloat(line.unit_price), AUD.exponent, dineroRoundingMethod)
      const basePrice = transformScale(multiplyMoneyByFloat(unitPrice, line.quantity), AUD.exponent, dineroRoundingMethod)
      const discountAmt = transformScale(multiplyMoneyByFloat(basePrice, line.discount_rate / 100), AUD.exponent, dineroRoundingMethod)

      // These two below WILL contain tax if the taxType == INCLUSIVE
      const totalBeforeTax = basePrice
      const totalBeforeTaxIncDiscount = transformScale(subtract(basePrice, discountAmt), AUD.exponent, dineroRoundingMethod)

      let taxAmt = initEmptyDineroObj()
      let taxAmtIncDiscount = initEmptyDineroObj()

      let finalLineAmt = totalBeforeTax
      let finalLineAmtIncDiscount = totalBeforeTaxIncDiscount

      if (docTaxType === 'INCLUSIVE') {
        taxAmt = calculateInclusiveTax(totalBeforeTax, taxRatePercent)
        taxAmtIncDiscount = calculateInclusiveTax(totalBeforeTaxIncDiscount, taxRatePercent)
      }
      else if (docTaxType === 'EXCLUSIVE') {
        taxAmt = calculateExclusiveTax(totalBeforeTax, taxRatePercent)
        taxAmtIncDiscount = calculateExclusiveTax(totalBeforeTaxIncDiscount, taxRatePercent)
        finalLineAmt = add(totalBeforeTax, taxAmt)
        finalLineAmtIncDiscount = add(totalBeforeTaxIncDiscount, taxAmtIncDiscount)
      }

      return {
        basePrice,
        discountAmt,
        totalBeforeTax,
        totalBeforeTaxIncDiscount,
        taxRatePercent,
        taxAmt,
        taxAmtIncDiscount,
        finalLineAmt,
        finalLineAmtIncDiscount
      }
    }
  }
}
